import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import ProgressPhotos from "@/components/ProgressPhotos";
import UnitToggle from "@/components/UnitToggle";
import { useUnits } from "@/lib/useUnits";
import { kgToLb } from "@/lib/units";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";

export default function ProgressPage() {
  const [weights, setWeights] = useState([]);
  const [stats, setStats] = useState(null);
  const { isImperial, fmtWeight, units, setUnits } = useUnits();

  useEffect(() => {
    (async () => {
      const [w, s] = await Promise.all([api.get("/weight"), api.get("/workouts/stats")]);
      setWeights(w.data);
      setStats(s.data);
    })();
  }, []);

  const weightSeries = weights.map((w) => ({
    date: new Date(w.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    weight: isImperial ? Math.round(kgToLb(w.weight_kg) * 10) / 10 : w.weight_kg,
  }));

  const volumeSeries = (stats?.volume_trend || []).map((v) => ({
    date: new Date(v.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    volume: Math.round(v.volume),
  }));

  return (
    <div className="space-y-6">
      <div>
        <div className="overline text-blaze">/ Progress</div>
        <h1 className="font-heading text-5xl uppercase mt-1">The signal in your data.</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="obs-card">
          <div className="flex items-center justify-between mb-1">
            <div className="overline">Weight trend</div>
            <UnitToggle units={units} onChange={setUnits} testid="progress-unit-toggle" />
          </div>
          <h3 className="font-heading text-2xl uppercase mb-4">Bodyweight</h3>
          {weightSeries.length > 1 ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weightSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis dataKey="date" stroke="#71717A" fontSize={11} />
                  <YAxis stroke="#71717A" fontSize={11} domain={["auto", "auto"]} />
                  <Tooltip contentStyle={{ background: "#121212", border: "1px solid #27272A" }} />
                  <Line type="monotone" dataKey="weight" stroke="#FF3B30" strokeWidth={2} dot={{ fill: "#FF3B30", r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-72 grid place-items-center text-zinc-500 text-sm uppercase tracking-widest">
              Need ≥ 2 weight entries
            </div>
          )}
        </div>

        <div className="obs-card">
          <div className="overline mb-1">Training volume</div>
          <h3 className="font-heading text-2xl uppercase mb-4">Per session (kg)</h3>
          {volumeSeries.length > 0 ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={volumeSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis dataKey="date" stroke="#71717A" fontSize={11} />
                  <YAxis stroke="#71717A" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#121212", border: "1px solid #27272A" }} />
                  <Bar dataKey="volume" fill="#FF3B30" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-72 grid place-items-center text-zinc-500 text-sm uppercase tracking-widest">
              Log workouts to see volume
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="stat-tile">
          <div className="overline">Total workouts</div>
          <div className="font-data text-3xl">{stats?.total_workouts ?? 0}</div>
        </div>
        <div className="stat-tile">
          <div className="overline">Personal records</div>
          <div className="font-data text-3xl">{stats ? Object.keys(stats.personal_records).length : 0}</div>
        </div>
        <div className="stat-tile">
          <div className="overline">Weight entries</div>
          <div className="font-data text-3xl">{weights.length}</div>
        </div>
        <div className="stat-tile">
          <div className="overline">Last weight</div>
          <div className="font-data text-3xl">{weights.length ? fmtWeight(weights[weights.length - 1].weight_kg) : "—"}</div>
        </div>
      </div>

      <ProgressPhotos />
    </div>
  );
}
